import { DialogAdminTeamCreation } from "@/components/admin/dialog-admin-team-creation";
import { DialogAdminUserInvitation } from "@/components/admin/dialog-admin-user-invitation";
import { DialogTeamCreation } from "@/components/admin/dialog-team-creation";
import AvatarCompany from "@/components/company/avatar-company";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { SidebarMenuButton } from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { getTeamUserRoleLabel } from "@/enums/team-user-role";
import { useCloseOnHistoryBack } from "@/hooks/useCloseOnHistoryBack";
import { useTranslation } from "@/hooks/useTranslation";
import { groupSwitchableTeams, isTeamListed } from "@/lib/team-switcher";
import { cn } from "@/lib/utils";
import { AdminTeamDTO, CompanyDTO, TeamDTO } from "@shaarp/common";
import { CheckIcon, ChevronsUpDownIcon, PlusIcon } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

/** Past this many teams the sheet offers a search, as the desktop does. */
const SEARCH_FROM_TEAMS = 5;

type TeamDialog = "adminTeam" | "user" | "team";

/**
 * The team switcher of the mobile sidebar: the team on show, and a bottom
 * sheet to pick another — the same sections as the desktop's popover (own,
 * advised, supported in admin mode) in rows a thumb can hit, with nothing
 * left to a tooltip. Picking a team reloads the app on it, as on the desktop.
 */
export function MobileTeamSwitcher() {
  const { t } = useTranslation();
  const { teams, teamSelected, teamCompanySelected, setTeamIdSelected } =
    useTeams();
  const { isAdminMode } = useAdminMode();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showsDisabled, setShowsDisabled] = useState(false);
  const [dialog, setDialog] = useState<TeamDialog | null>(null);
  // Stacked over the sidebar's sheet: the back gesture closes the dialog,
  // not the sidebar with a half-filled form in it.
  useCloseOnHistoryBack(dialog !== null, () => setDialog(null));

  const { own, affiliated, supported } = useMemo(
    () => groupSwitchableTeams(teams ?? []),
    [teams],
  );

  if (!teams?.length) return null;

  const matches = (team: TeamDTO) =>
    team.name.toLowerCase().includes(search.trim().toLowerCase());
  const listed = (sectionTeams: TeamDTO[]) =>
    sectionTeams.filter(
      (team) =>
        matches(team) &&
        // A search finds disabled teams too, as on the desktop.
        isTeamListed(team, showsDisabled || search.trim() !== ""),
    );

  const selectTeam = (team: TeamDTO) => {
    setTeamIdSelected(team.id);
    window.location.reload();
  };

  const openDialog = (next: TeamDialog) => {
    setIsOpen(false);
    setDialog(next);
  };

  const sections: { key: string; title: string; teams: TeamDTO[] }[] = [
    { key: "own", title: t("teamSwitcher.teams"), teams: listed(own) },
    ...(user?.isAdvisor
      ? [
          {
            key: "affiliated",
            title: t("teamSwitcher.affiliatedTeams"),
            teams: listed(affiliated),
          },
        ]
      : []),
    ...(isAdminMode
      ? [
          {
            key: "supported",
            title: t("teamSwitcher.otherTeamsSupport"),
            teams: listed(supported),
          },
        ]
      : []),
  ];
  const hasResults = sections.some((section) => section.teams.length > 0);

  return (
    <>
      <SidebarMenuButton
        size="touch"
        aria-label={t("teamSwitcher.selectTeam")}
        onClick={() => setIsOpen(true)}
      >
        <AvatarCompany
          company={
            teamCompanySelected ?? ({ name: teamSelected?.name } as CompanyDTO)
          }
          className="size-6 shrink-0 text-[10px]"
        />
        <span>{teamSelected?.name}</span>
        <ChevronsUpDownIcon className="ml-auto opacity-50" />
      </SidebarMenuButton>

      <MobileSheet
        open={isOpen}
        onOpenChange={setIsOpen}
        heightClass="h-[80dvh]"
      >
        <div className="flex flex-col gap-3 px-4 pt-1 pb-3 shrink-0">
          <SheetTitle className="text-lg">
            {t("teamSwitcher.selectTeam")}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t("teamSwitcher.selectTeam")}
          </SheetDescription>
          {teams.length > SEARCH_FROM_TEAMS && (
            <Input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("teamSwitcher.search")}
              aria-label={t("teamSwitcher.search")}
              className="h-11 text-base"
            />
          )}
          {isAdminMode && (
            <div className="flex justify-between items-center min-h-11">
              <Label htmlFor="mobile-team-switcher-disabled">
                {t("teamSwitcher.showDisabled")}
              </Label>
              <Switch
                id="mobile-team-switcher-disabled"
                checked={showsDisabled}
                onCheckedChange={setShowsDisabled}
              />
            </div>
          )}
        </div>
        <div className="overflow-y-auto overscroll-y-contain flex-1 px-2 pb-4 min-h-0">
          {!hasResults && (
            <p className="px-2 py-6 text-center text-muted-foreground">
              {t("teamSwitcher.noTeamsFound")}
            </p>
          )}
          {sections.map(
            (section) =>
              section.teams.length > 0 && (
                <section key={section.key} className="pb-3">
                  <h3 className="px-2 pb-1 text-xs font-medium text-muted-foreground">
                    {section.title}
                  </h3>
                  <ul>
                    {section.teams.map((team) => (
                      <li key={team.id}>
                        <TeamRow
                          team={team}
                          isSelected={team.id === teamSelected?.id}
                          showsRole={section.key !== "supported"}
                          onSelect={() => selectTeam(team)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ),
          )}
          {isAdminMode && (
            <ActionRow onClick={() => openDialog("adminTeam")}>
              {t("teamSwitcher.newTeamAdmin")}
            </ActionRow>
          )}
          {isAdminMode && (
            <ActionRow onClick={() => openDialog("user")}>
              {t("teamSwitcher.newUser")}
            </ActionRow>
          )}
          {user?.isAdvisor && (
            <ActionRow onClick={() => openDialog("team")}>
              {t("teamSwitcher.newTeam")}
            </ActionRow>
          )}
        </div>
      </MobileSheet>

      <DialogAdminTeamCreation
        open={dialog === "adminTeam"}
        onCreated={(newTeam: AdminTeamDTO) => {
          setTeamIdSelected(newTeam.id);
          window.location.reload();
        }}
        onClose={() => setDialog(null)}
      />
      <DialogAdminUserInvitation
        open={dialog === "user"}
        onClose={() => setDialog(null)}
      />
      <DialogTeamCreation
        open={dialog === "team"}
        onClose={() => setDialog(null)}
      />
    </>
  );
}

function TeamRow({
  team,
  isSelected,
  showsRole,
  onSelect,
}: {
  readonly team: TeamDTO;
  readonly isSelected: boolean;
  readonly showsRole: boolean;
  readonly onSelect: () => void;
}) {
  const { t } = useTranslation();
  const isDisabled = team.status === "disabled";

  return (
    <Button
      variant="ghost"
      onClick={onSelect}
      aria-current={isSelected || undefined}
      className={cn(
        "justify-start gap-3 px-2 py-2 w-full h-auto font-normal text-left rounded-lg min-h-11",
        isSelected && "bg-accent",
      )}
    >
      <AvatarCompany
        company={
          {
            name: team.name,
            logo: team.companyLogo || null,
            domain: team.companyDomain || null,
          } as CompanyDTO
        }
        className="size-7 shrink-0 text-[10px]"
      />
      <span className="flex flex-col flex-1 min-w-0">
        <span className="text-base truncate">{team.name}</span>
        {/* Said in the row, not in a tooltip a finger cannot reach. */}
        {isDisabled && (
          <span className="text-xs text-warning">
            {t("teamSwitcher.teamDisabled")}
          </span>
        )}
      </span>
      {showsRole && (
        <Badge variant="outline" className="shrink-0">
          {getTeamUserRoleLabel(team.userRole, t)}
        </Badge>
      )}
      <CheckIcon
        className={cn("size-5 shrink-0", !isSelected && "invisible")}
        aria-hidden
      />
    </Button>
  );
}

function ActionRow({
  onClick,
  children,
}: {
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className="justify-start gap-3 px-2 w-full h-11 text-base font-normal rounded-lg"
    >
      <PlusIcon className="size-5 shrink-0" />
      {children}
    </Button>
  );
}
